import {
  FieldErrors,
  Resolver,
  useController,
  useForm,
} from 'react-hook-form-v7';
import { useEditTemplatingItemMetadata } from './useEditTemplatingItemMetadata';
import { TemplatingItem, ModelUtils, TemplateSummary } from '../models';
import { useCallback } from 'react';
import { useValidateTemplatingItemName } from './useCreateTemplatingItemForm';
import { KnownImpressionTypes } from '../../SubjectViewer/Modules/AITools/ImpressionEditor/ImpressionItem';

type FormValues = {
  templatingItemName: string;
  impressionType: KnownImpressionTypes;
};

const useResolver = (type: TemplatingItem['type']): Resolver<FormValues> => {
  const validateTemplatingItemName = useValidateTemplatingItemName();

  return useCallback(
    (data: FormValues) => {
      const errors: FieldErrors<FormValues> = {};

      validateTemplatingItemName(type, data.templatingItemName, errors);

      return {
        values: data,
        errors,
      };
    },
    [type, validateTemplatingItemName],
  );
};

export const useEditTemplatingItemMetadataForm = ({
  templatingItem,
  onSuccess,
}: {
  templatingItem: TemplatingItem;
  onSuccess?: () => void;
}) => {
  const resolver = useResolver(templatingItem.type);
  const templatingItemName = ModelUtils.getName(templatingItem);

  const {
    handleSubmit,
    control,
    formState,
    reset: resetForm,
  } = useForm<FormValues>({
    defaultValues: {
      templatingItemName,
      impressionType: (templatingItem.type === 'template'
        ? (templatingItem as TemplateSummary).impressionType
        : 'general') as KnownImpressionTypes,
    },
    resolver,
    mode: 'onChange',
  });

  const handleSuccess = useCallback(() => {
    resetForm();
    onSuccess?.();
  }, [resetForm, onSuccess]);

  const { editTemplatingItemMetadata } = useEditTemplatingItemMetadata({
    onSuccess: handleSuccess,
  });

  const templatingItemNameControl = useController({
    control,
    name: 'templatingItemName',
  });

  const impressionTypeControl = useController({
    control,
    name: 'impressionType',
  });

  const onSubmit = handleSubmit(data => {
    editTemplatingItemMetadata(templatingItem, {
      name: data.templatingItemName,
      impressionType: data.impressionType,
    });
  });

  const hasError = !formState.isValid;

  const reset = useCallback(() => {
    resetForm({
      templatingItemName: templatingItemName,
    });
  }, [resetForm, templatingItemName]);

  return {
    templatingItemNameControl,
    impressionTypeControl,
    onSubmit,
    hasError,
    isDirty: formState.isDirty,
    reset,
  };
};
